// Full-screen "Setting Up Match" timeline shown while a new match is sent to its server
// (match_setup_progress WebSocket steps 1-8, -1 = failed). Moved unchanged out of Matches.js.
import React from 'react';
import { useTranslation } from 'react-i18next';

export default function MatchSetupProgress({ step, label, hasBackup, serverInfo }) {
  const { t } = useTranslation();
  const TOTAL = 8;
  const STEPS = [
    { label: t('matchSetup.deployAvatars', 'Deploying player avatars') },
    { label: t('matchSetup.sendingMatch', 'Sending match to server') },
    { label: t('matchSetup.configuringLogs', 'Configuring match logs') },
    { label: t('matchSetup.enforcingNames', 'Enforcing player names') },
    { label: t('matchSetup.backupPrefix', 'Setting backup prefix') },
    { label: t('matchSetup.uploadingBackups', 'Uploading map backups') },
    { label: t('matchSetup.readyForWarmup', 'Ready for warmup') },
    { label: t('matchSetup.done', 'Done') },
  ];
  const SKIP_STEPS = [1, 4];
  const isError = step === -1;
  const isDone = step >= TOTAL;
  const sInfo = serverInfo;

  // styles: .cmp-setup* in createMatch.css
  return (
    <div className="cmp-setup" role="status" aria-live="polite">
      <div className="cmp-setup-head">
        <div className="cmp-setup-title">
          {isError ? t('matchSetup.setupFailed', 'Setup Failed') : isDone ? t('matchSetup.matchReady', 'Match Ready!') : t('matchSetup.settingUp', 'Setting Up Match')}
        </div>
        <div className="cmp-setup-sub">
          {isError ? (label || t('matchSetup.errorOccurred', 'An error occurred')) : isDone && sInfo ? `${sInfo.ip}:${sInfo.port || 27015}` : t('matchSetup.stepOf', 'Step {{current}} of {{total}}', { current: Math.max(1, step), total: TOTAL })}
        </div>
      </div>

      <div className="cmp-setup-steps">
        {STEPS.map((s, i) => {
          const stepNum = i + 1;
          // the server sends step N when step N STARTS: done = a later step started, active = this one runs
          const done = step > stepNum || isDone;
          const active = step === stepNum && !isError && !isDone;
          const skipped = !hasBackup && SKIP_STEPS.includes(stepNum) && done;
          const failed = isError && step === -1 && i === Math.max(0, (parseInt(label?.match(/\d/)?.[0]) || 1) - 1);
          const state = failed ? ' failed' : done ? ' done' : active ? ' active' : '';
          return (
            <div key={i} className={`cmp-setup-step${state}`}>
              <div className="cmp-setup-rail">
                <div className="cmp-setup-dot">
                  {/* inline check / step number - the app doesn't load an icon font */}
                  {done && !skipped
                    ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg>
                    : stepNum}
                </div>
                {i < STEPS.length - 1 && <div className="cmp-setup-line" />}
              </div>
              <div className="cmp-setup-text">
                {skipped ? <em>{t('matchSetup.skippedNoSftp', 'Skipped - No SFTP/FTP')}</em> : s.label}
                {active && <div className="cmp-setup-wait" aria-hidden="true"><span /><span /><span /></div>}
              </div>
            </div>
          );
        })}
      </div>

      <div className={`cmp-setup-bar${isError ? ' failed' : isDone ? ' done' : ''}`}>
        <span style={{ transform: `scaleX(${isError ? 1 : Math.max(0, step) / TOTAL})` }} />
      </div>
    </div>
  );
}
